import { motion } from 'framer-motion';
import { Film, Award, Users, Target, Star, Heart } from 'lucide-react';

const LOGO_URL = "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512904/456542092_122097483962499699_7816098867247799670_n_d0jy4p.jpg";
const PRODUCER_IMAGE = "https://res.cloudinary.com/drcoefnzk/image/upload/v1771696777/Swarnali.jpg_bzhwrf.jpg";
const COPRODUCER_IMAGE = "https://res.cloudinary.com/drcoefnzk/image/upload/v1773768430/WhatsApp_Image_2026-03-15_at_3.58.27_PM_c0lu9k.jpg";

const IMAGES = {
  premiere1: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512906/470266213_122132673044499699_4752606058197197552_n_x012lo.jpg",
  premiere2: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512906/470261869_122132666054499699_6053191376983885333_n_rvphve.jpg",
  audience: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512908/469915718_122132673578499699_4338502581283027790_n_g70az7.jpg",
  hero: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512905/469925403_122132677226499699_1942442291909231329_n_pddcc0.jpg",
};

const About = () => {
  const values = [
    {
      icon: Film,
      title: 'Authentic Storytelling',
      description: 'We believe in stories rooted in truth and emotion, crafted to resonate with audiences across generations.',
    },
    {
      icon: Star,
      title: 'Creative Excellence',
      description: 'Every frame is meticulously crafted to achieve the highest standards of cinematic quality.',
    },
    {
      icon: Users,
      title: 'Nurturing Talent',
      description: 'We provide platforms for emerging artists to showcase their abilities and grow in the industry.',
    },
    {
      icon: Heart,
      title: 'Passion for Cinema',
      description: 'Our love for Hindi cinema drives us to push boundaries and explore new narratives.',
    },
  ];

  const milestones = [
    { year: '2024', event: 'Released "Aprokashito" - Our debut feature film' },
    { year: '2024', event: 'Theatrical release across West Bengal' },
    { year: '2026', event: 'Upcoming Hindi film project' },
  ];

  return (
    <div data-testid="about-page" className="min-h-screen bg-[#050505] pt-20">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ backgroundImage: `url(${IMAGES.hero})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="text-[#E50914] text-sm tracking-[0.3em] uppercase font-semibold">About Us</span>
            <h1 className="font-heading text-6xl md:text-8xl text-white tracking-tight mt-4 mb-6">
              SWARNALI
              <span className="block text-gold-gradient">ENTERTAINMENT</span>
            </h1>
            <p className="text-[#A3A3A3] text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
              A Kolkata-based film production house dedicated to crafting compelling narratives 
              that push the boundaries of Hindi cinema.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-[#E50914] text-sm tracking-[0.3em] uppercase font-semibold">Our Story</span>
              <h2 className="font-heading text-4xl md:text-5xl text-white tracking-wide mt-4 mb-6">
                FROM VISION TO SCREEN
              </h2>
              <div className="space-y-4 text-[#A3A3A3] leading-relaxed">
                <p>
                  Swarnali Entertainment was born from a passion for storytelling and a deep love for 
                  Hindi cinema. Founded by producer <span className="text-[#D4AF37] font-semibold">Swarnali Acharya</span>, 
                  our production house is committed to bringing unique, thought-provoking stories to the screen.
                </p>
                <p>
                  Based in the heart of Kolkata, the cultural capital of India, we draw inspiration from 
                  the rich literary and artistic heritage of Bengal. Our debut feature film, 
                  <span className="font-accent italic text-white"> Aprokashito</span>, released in 
                  September 2024, marked our entry into the world of feature filmmaking.
                </p>
                <p>
                  Directed by the talented Soham Acharya, Aprokashito showcased our commitment to 
                  quality storytelling and established Swarnali Entertainment as a production house 
                  to watch in the Hindi film industry.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img
                  src={IMAGES.premiere1}
                  alt="Swarnali Entertainment Team"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 w-48 h-48 rounded-xl overflow-hidden border-4 border-[#050505]">
                <img
                  src={LOGO_URL}
                  alt="Swarnali Entertainment Logo"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Producer Section */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-[#E50914] text-sm tracking-[0.3em] uppercase font-semibold">Leadership</span>
            <h2 className="font-heading text-4xl md:text-5xl text-white tracking-wide mt-4">
              THE VISIONARY BEHIND
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-[#121212] to-[#1a1a1a] rounded-2xl p-6 md:p-12 max-w-5xl mx-auto"
          >
            <div className="flex flex-col lg:flex-row items-center lg:items-start gap-8">
              <div className="w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden border-4 border-[#D4AF37] flex-shrink-0">
                <img
                  src={PRODUCER_IMAGE}
                  alt="Swarnali Chakraborty Acharya"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="text-center lg:text-left flex-1">
                <h3 className="font-heading text-2xl md:text-3xl text-white tracking-wider mb-2">SWARNALI CHAKRABORTY ACHARYA</h3>
                <p className="text-[#D4AF37] font-accent text-lg italic mb-6">Founder & Producer</p>
                <div className="space-y-4 text-[#A3A3A3] leading-relaxed text-sm md:text-base">
                  <p>
                    A visionary entrepreneur with a diverse portfolio, Swarnali Chakraborty Acharya began her journey as a young B.Com graduate with a determined mindset to achieve significant milestones in her career.
                  </p>
                  <p>
                    She further honed her expertise by pursuing an MBA in Marketing, combining her financial management skills with sharp marketing acumen gained through hands-on experience. This foundation led her to explore joint ventures, giving birth to <span className="text-white font-semibold">SAS Orchid Worldwide LLP</span> — a successful enterprise in Export-Import Trading and Real Estate Development.
                  </p>
                  <p>
                    Building on this success, her entrepreneurial spirit led to the creation of <span className="text-[#D4AF37] font-semibold">Swarnali Entertainment</span> — with a singular aim to create a lasting legacy in the world of cinema. Her passion for storytelling and commitment to quality has established the production house as a rising name in Hindi cinema.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Co-Producer Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-[#121212] to-[#1a1a1a] rounded-2xl p-6 md:p-12 max-w-5xl mx-auto"
          >
            <div className="flex flex-col lg:flex-row items-center lg:items-start gap-8">
              <div className="w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden border-4 border-[#E50914] flex-shrink-0">
                <img
                  src={COPRODUCER_IMAGE}
                  alt="Soham Acharya"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="text-center lg:text-left flex-1">
                <h3 className="font-heading text-2xl md:text-3xl text-white tracking-wider mb-2">SOHAM ACHARYA</h3>
                <p className="text-[#E50914] font-accent text-lg italic mb-6">Co-Producer & Director</p>
                <div className="space-y-4 text-[#A3A3A3] leading-relaxed text-sm md:text-base">
                  <p>
                    Soham Acharya is an Indian producer, director, and screenwriter who made his mark in the film industry through his award-winning short film <span className="text-[#D4AF37] font-semibold">'Order-Supply'</span>.
                  </p>
                  <p>
                    His contribution as a debutant director and co-producer of the feature film <span className="text-white font-semibold">'Aprokashito'</span> stands as a significant milestone in his creative journey. With a keen eye for storytelling and a passion for cinema, Soham continues to push the boundaries of Hindi filmmaking.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-[#E50914] text-sm tracking-[0.3em] uppercase font-semibold">What Drives Us</span>
            <h2 className="font-heading text-4xl md:text-5xl text-white tracking-wide mt-4">
              OUR VALUES
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-[#121212] border border-white/5 p-8 rounded-xl hover:border-[#E50914]/30 transition-colors group"
              >
                <value.icon className="text-[#D4AF37] mb-4 group-hover:scale-110 transition-transform" size={32} />
                <h3 className="text-white font-semibold text-lg mb-3">{value.title}</h3>
                <p className="text-[#A3A3A3] text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-[#E50914] text-sm tracking-[0.3em] uppercase font-semibold">Our Journey</span>
            <h2 className="font-heading text-4xl md:text-5xl text-white tracking-wide mt-4">
              MILESTONES
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto">
            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="flex items-start gap-6 mb-8"
              >
                <div className="flex-shrink-0 w-20 h-20 rounded-full bg-[#E50914]/10 border border-[#E50914]/30 flex items-center justify-center">
                  <span className="font-heading text-2xl text-[#E50914]">{milestone.year}</span>
                </div>
                <div className="pt-5">
                  <p className="text-white text-lg">{milestone.event}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-24 bg-[#0a0a0a] relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <Target className="text-[#D4AF37] mx-auto mb-6" size={48} />
            <h2 className="font-heading text-4xl md:text-5xl text-white tracking-wide mb-6">
              OUR VISION
            </h2>
            <p className="text-[#A3A3A3] text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
              To establish Swarnali Entertainment as a leading production house that creates 
              cinema transcending regional boundaries. With our upcoming Hindi film project in 2026, 
              we aim to bring the essence of our storytelling to a wider, pan-Indian audience while 
              continuing to champion Hindi cinema.
            </p>

            <div className="mt-12 flex justify-center gap-8 flex-wrap">
              <div className="text-center">
                <span className="font-heading text-5xl text-[#E50914]">1+</span>
                <p className="text-[#A3A3A3] text-sm uppercase tracking-wider mt-2">Feature Films</p>
              </div>
              <div className="text-center">
                <span className="font-heading text-5xl text-[#D4AF37]">2026</span>
                <p className="text-[#A3A3A3] text-sm uppercase tracking-wider mt-2">Hindi Film Launch</p>
              </div>
              <div className="text-center">
                <span className="font-heading text-5xl text-[#E50914]">∞</span>
                <p className="text-[#A3A3A3] text-sm uppercase tracking-wider mt-2">Stories to Tell</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
